import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Loader2, RefreshCw, Clock, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, Filter, ArrowLeft } from 'lucide-react'
import { Link } from 'react-router-dom'
import { AppShell } from '@/components/shell'
import { useAuth } from '@/context/AuthContext'
import { supabase } from '@/integrations/supabase/client'

interface AuditLog {
  id: string
  user_id: string | null
  league_id: string | null
  action: string
  entity_type: string | null
  entity_id: string | null
  details: Record<string, unknown> | null
  created_at: string
}

const ACTION_FILTERS = [
  { value: '', label: 'All Actions' },
  { value: 'pick.submitted', label: 'Pick Submitted' },
  { value: 'autopick.assigned', label: 'Autopick Assigned' },
  { value: 'admin.pick_overridden', label: 'Pick Override' },
  { value: 'auth.magic_link_sent', label: 'Magic Link Sent' },
  { value: 'invite.sent', label: 'Invite Sent' },
  { value: 'sync.fixtures_completed', label: 'Sync Completed' },
  { value: 'notification.picks_open', label: 'Picks-Open Notification' },
]

const ACTION_BADGES: Record<string, { label: string; color: string }> = {
  'pick.submitted': { label: 'Pick', color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400' },
  'autopick.assigned': { label: 'Autopick', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400' },
  'admin.pick_overridden': { label: 'Override', color: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400' },
  'auth.magic_link_sent': { label: 'Magic Link', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400' },
  'invite.sent': { label: 'Invite', color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-400' },
  'sync.fixtures_completed': { label: 'Sync', color: 'bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-400' },
  'notification.picks_open': { label: 'Notification', color: 'bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-400' },
}

const PAGE_SIZE = 15

export default function AdminActivityLog() {
  const { isSuperAdmin } = useAuth()
  const [expandedLogId, setExpandedLogId] = useState<string | null>(null)
  const [actionFilter, setActionFilter] = useState<string>('')
  const [page, setPage] = useState(0)

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['audit-logs', page, actionFilter],
    queryFn: async () => {
      let query = supabase
        .from('audit_logs')
        .select('*', { count: 'exact' })
        .order('created_at', { ascending: false })
        .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1)

      if (actionFilter) {
        query = query.eq('action', actionFilter)
      }

      const { data: rawLogs, error, count } = await query
      if (error) throw error

      const logs = (rawLogs ?? []) as AuditLog[]

      // Batch-resolve user and league names
      const userIds = [...new Set(logs.map(l => l.user_id).filter(Boolean))] as string[]
      const leagueIds = [...new Set(logs.map(l => l.league_id).filter(Boolean))] as string[]

      const userNames: Record<string, string> = {}
      const leagueNames: Record<string, string> = {}

      if (userIds.length > 0) {
        const { data: members } = await supabase
          .from('league_memberships')
          .select('user_id, display_name')
          .in('user_id', userIds)
        if (members) {
          for (const m of members) {
            if (m.display_name && !userNames[m.user_id]) {
              userNames[m.user_id] = m.display_name
            }
          }
        }
      }

      if (leagueIds.length > 0) {
        const { data: leagues } = await supabase
          .from('leagues')
          .select('id, name')
          .in('id', leagueIds)
        if (leagues) {
          for (const l of leagues) {
            leagueNames[l.id] = l.name
          }
        }
      }

      return { logs, totalCount: count ?? 0, userNames, leagueNames }
    },
    refetchInterval: 60000,
  })

  const logs = data?.logs ?? []
  const totalCount = data?.totalCount ?? 0
  const totalPages = Math.ceil(totalCount / PAGE_SIZE)
  const userNames = data?.userNames ?? {}
  const leagueNames = data?.leagueNames ?? {}

  if (!isSuperAdmin) {
    return (
      <AppShell>
        <div className="text-center py-16 text-zinc-500 dark:text-zinc-400">
          <p className="text-lg font-medium">Access Denied</p>
          <p className="text-sm mt-1">This page is only available to super admins.</p>
        </div>
      </AppShell>
    )
  }

  return (
    <AppShell>
      <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <Link
            to="/admin"
            className="p-2 -ml-2 text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300 transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div className="flex-1">
            <h1 className="text-xl font-semibold text-zinc-900 dark:text-zinc-100">Activity Log</h1>
            <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-0.5">
              {totalCount} total events
            </p>
          </div>
          <button
            onClick={() => refetch()}
            className="p-2 text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300 transition-colors"
          >
            <RefreshCw className="h-4 w-4" />
          </button>
        </div>

        {/* Filter */}
        <div className="flex flex-wrap gap-3">
          <div className="relative min-w-[200px]">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
            <select
              value={actionFilter}
              onChange={(e) => {
                setActionFilter(e.target.value)
                setPage(0)
              }}
              className="w-full pl-9 pr-8 py-2 text-sm rounded-xl border border-zinc-200 dark:border-zinc-700
                         bg-white dark:bg-zinc-900 text-zinc-900 dark:text-zinc-100
                         focus:outline-none focus:ring-2 focus:ring-emerald-500/40 appearance-none"
            >
              {ACTION_FILTERS.map((f) => (
                <option key={f.value} value={f.value}>
                  {f.label}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400 pointer-events-none" />
          </div>
        </div>

        {/* Logs list */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-zinc-400" />
          </div>
        ) : logs.length === 0 ? (
          <div className="text-center py-12 text-sm text-zinc-500 dark:text-zinc-400">
            {actionFilter
              ? 'No events match your filter.'
              : 'No activity logged yet.'}
          </div>
        ) : (
          <div className="space-y-2">
            {logs.map((log) => {
              const isExpanded = expandedLogId === log.id
              const date = new Date(log.created_at)
              const timeAgo = getTimeAgo(date)
              const badge = ACTION_BADGES[log.action] ?? { label: log.action, color: 'bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400' }
              const userName = log.user_id ? userNames[log.user_id] : null
              const leagueName = log.league_id ? leagueNames[log.league_id] : null
              const summary = getSummary(log, userName)

              return (
                <div
                  key={log.id}
                  className="rounded-xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 transition-colors"
                >
                  <button
                    onClick={() => setExpandedLogId(isExpanded ? null : log.id)}
                    className="w-full px-4 py-3 flex items-center gap-3 text-left"
                  >
                    {/* Action badge */}
                    <span className={`text-[10px] px-2 py-0.5 rounded-md font-medium uppercase tracking-wide shrink-0 ${badge.color}`}>
                      {badge.label}
                    </span>

                    {/* Summary */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-zinc-900 dark:text-zinc-100 truncate">
                        {summary}
                      </p>
                    </div>

                    {/* Time + expand */}
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-xs text-zinc-500 dark:text-zinc-400 flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {timeAgo}
                      </span>
                      {isExpanded ? (
                        <ChevronUp className="h-4 w-4 text-zinc-400" />
                      ) : (
                        <ChevronDown className="h-4 w-4 text-zinc-400" />
                      )}
                    </div>
                  </button>

                  {/* Expanded details */}
                  {isExpanded && (
                    <div className="px-4 pb-3 pt-0 border-t border-zinc-100 dark:border-zinc-800">
                      <div className="mt-3 space-y-2 text-xs">
                        <div className="flex justify-between">
                          <span className="text-zinc-500 dark:text-zinc-400">ID</span>
                          <span className="text-zinc-700 dark:text-zinc-300 font-mono text-[10px]">{log.id}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-zinc-500 dark:text-zinc-400">Timestamp</span>
                          <span className="text-zinc-700 dark:text-zinc-300 font-mono">{date.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-zinc-500 dark:text-zinc-400">Action</span>
                          <span className="text-zinc-700 dark:text-zinc-300">{log.action}</span>
                        </div>
                        {log.user_id && (
                          <div className="flex justify-between">
                            <span className="text-zinc-500 dark:text-zinc-400">User</span>
                            <span className="text-zinc-700 dark:text-zinc-300">
                              {userName ?? <span className="font-mono text-[10px]">{log.user_id}</span>}
                            </span>
                          </div>
                        )}
                        {log.league_id && (
                          <div className="flex justify-between">
                            <span className="text-zinc-500 dark:text-zinc-400">League</span>
                            <span className="text-zinc-700 dark:text-zinc-300">
                              {leagueName ?? <span className="font-mono text-[10px]">{log.league_id}</span>}
                            </span>
                          </div>
                        )}
                        {log.entity_type && (
                          <div className="flex justify-between">
                            <span className="text-zinc-500 dark:text-zinc-400">Entity Type</span>
                            <span className="text-zinc-700 dark:text-zinc-300">{log.entity_type}</span>
                          </div>
                        )}
                        {log.entity_id && (
                          <div className="flex justify-between">
                            <span className="text-zinc-500 dark:text-zinc-400">Entity ID</span>
                            <span className="text-zinc-700 dark:text-zinc-300 font-mono text-[10px]">{log.entity_id}</span>
                          </div>
                        )}
                        {log.details && Object.keys(log.details).length > 0 && (
                          <div className="mt-2 pt-2 border-t border-zinc-100 dark:border-zinc-800">
                            <span className="text-zinc-500 dark:text-zinc-400 font-medium">Details</span>
                            <div className="mt-1 space-y-1">
                              {Object.entries(log.details).map(([key, value]) => (
                                <div key={key} className="flex justify-between">
                                  <span className="text-zinc-500 dark:text-zinc-400">{key}</span>
                                  <span className="text-zinc-700 dark:text-zinc-300 font-mono text-[10px] max-w-[60%] text-right break-all">
                                    {typeof value === 'object' ? JSON.stringify(value) : String(value ?? '')}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between pt-4 border-t border-zinc-200 dark:border-zinc-800">
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg
                         text-zinc-600 dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-800
                         disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </button>

            <span className="text-sm text-zinc-500 dark:text-zinc-400">
              Page {page + 1} of {totalPages}
            </span>

            <button
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg
                         text-zinc-600 dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-800
                         disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>
    </AppShell>
  )
}

function getSummary(log: AuditLog, userName: string | null): string {
  const d = log.details as Record<string, unknown> | null
  const who = userName ? `${userName} — ` : ''

  switch (log.action) {
    case 'pick.submitted':
      return `${who}MD${d?.matchday ?? '?'} — ${d?.team ?? 'unknown team'}${d?.doubleDown ? ' (DD)' : ''}`
    case 'autopick.assigned':
      return `${who}MD${d?.matchday ?? '?'} — ${d?.team ?? 'unknown team'} (missed deadline)`
    case 'admin.pick_overridden':
      return `${who}MD${d?.matchday ?? '?'} — ${d?.team ?? '?'}${d?.previousTeam ? ` (was ${d.previousTeam})` : ''}`
    case 'auth.magic_link_sent':
      return `${who}Sent to ${d?.targetEmail ?? 'unknown'}`
    case 'invite.sent':
      return `${who}${d?.inviteeEmail ?? 'unknown'} → ${d?.leagueName ?? 'league'}`
    case 'sync.fixtures_completed':
      return `MD ${Array.isArray(d?.matchdays) ? (d.matchdays as number[]).join(', ') : '?'} — ${d?.fixturesUpdated ?? 0} fixtures`
    case 'notification.picks_open':
      return `MD${d?.nextMatchday ?? '?'} — ${d?.recipientCount ?? 0} recipients`
    default:
      return log.action
  }
}

function getTimeAgo(date: Date): string {
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000)

  if (seconds < 60) return 'just now'
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`
  return date.toLocaleDateString()
}
